import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Building2, Users, User, ChevronDown, ChevronRight, Loader2 } from 'lucide-react';

export const ReportTab = ({ report }) => {
  const [detailed, setDetailed] = useState(null);
  const [loadingDet, setLoadingDet] = useState(false);
  const [expandedComp, setExpandedComp] = useState({});
  const [expandedBen, setExpandedBen] = useState({});
  const [filterAnno, setFilterAnno] = useState(new Date().getFullYear());
  const [filterMese, setFilterMese] = useState('');

  const loadDetailed = useCallback(async () => {
    setLoadingDet(true);
    try {
      const params = new URLSearchParams();
      if (filterAnno) params.set('anno', filterAnno);
      if (filterMese) params.set('mese', filterMese);
      const r = await api.get(`/prestiti/report/dettagliato?${params.toString()}`);
      setDetailed(r.data);
    } catch (e) {
      toast.error('Errore caricamento report');
    } finally { setLoadingDet(false); }
  }, [filterAnno, filterMese]);

  useEffect(() => { loadDetailed(); }, [loadDetailed]);

  if (!report) return <div className="flex justify-center py-12"><Loader2 className="animate-spin text-slate-400" size={20} /></div>;

  const fmt = (n) => Number(n || 0).toLocaleString('it-IT', { minimumFractionDigits: 0, maximumFractionDigits: 2 });

  return (
    <div className="space-y-5">
      {/* Summary cards + Filtri */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-xs text-slate-400 font-medium uppercase">Provvigioni Pratiche</p>
          <p className="text-2xl font-bold text-green-700 mt-1">{fmt(report.totale_provvigioni_pratiche)} EUR</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-xs text-slate-400 font-medium uppercase">Provvigioni Mensili</p>
          <p className="text-2xl font-bold text-blue-700 mt-1">{fmt(report.totale_provvigioni_mensili)} EUR</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-xs text-slate-400 font-medium uppercase">Totale Generale {report.anno}</p>
          <p className="text-2xl font-bold text-slate-900 mt-1">{fmt(report.totale_generale)} EUR</p>
        </div>
      </div>

      {/* Filtri periodo */}
      <div className="bg-white rounded-xl border border-slate-200 p-3 flex flex-wrap items-center gap-2" data-testid="report-filters">
        <span className="text-xs text-slate-500 font-medium">Periodo drill-down:</span>
        <select value={filterAnno} onChange={e => setFilterAnno(Number(e.target.value))} className="px-2 py-1.5 text-xs border border-slate-200 rounded-lg bg-white" data-testid="filter-anno">
          {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(y => <option key={y} value={y}>{y}</option>)}
        </select>
        <select value={filterMese} onChange={e => setFilterMese(e.target.value)} className="px-2 py-1.5 text-xs border border-slate-200 rounded-lg bg-white" data-testid="filter-mese">
          <option value="">Tutti i mesi</option>
          {['Gennaio','Febbraio','Marzo','Aprile','Maggio','Giugno','Luglio','Agosto','Settembre','Ottobre','Novembre','Dicembre'].map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
        </select>
        {loadingDet && <Loader2 size={14} className="animate-spin text-slate-400" />}
        {detailed && !loadingDet && (
          <span className="ml-auto text-xs text-slate-500">
            {detailed.totale_pratiche} pratiche · Erogato {fmt(detailed.totale_erogato)} EUR · Provvigioni {fmt(detailed.totale_provvigioni)} EUR
          </span>
        )}
      </div>

      {/* DETTAGLIO PER COMPAGNIA (drill-down) */}
      {detailed && (
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-2 mb-3">
            <Building2 size={16} className="text-amber-600" />
            <h3 className="text-sm font-semibold text-slate-800">Dettaglio per Compagnia Finanziaria</h3>
            <span className="text-[11px] text-slate-400">(clicca su una riga per vedere le pratiche)</span>
          </div>
          {detailed.compagnie.length === 0 ? (
            <p className="text-center text-slate-400 text-xs py-6">Nessun dato per il periodo selezionato</p>
          ) : (
            <div className="space-y-2">
              {detailed.compagnie.map((c) => {
                const expanded = expandedComp[c.nome];
                return (
                  <div key={c.nome} className="border border-slate-200 rounded-lg overflow-hidden" data-testid={`report-compagnia-${c.nome}`}>
                    <button
                      onClick={() => setExpandedComp(s => ({ ...s, [c.nome]: !s[c.nome] }))}
                      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-amber-50 transition-colors text-left"
                    >
                      {expanded ? <ChevronDown size={14} className="text-slate-500" /> : <ChevronRight size={14} className="text-slate-500" />}
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-slate-800">{c.nome}</p>
                        <p className="text-[11px] text-slate-500">{c.count} pratiche · Erogato {fmt(c.totale_erogato)} EUR</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-slate-400">Provvigioni</p>
                        <p className="text-base font-bold text-green-700">{fmt(c.totale_provvigione)} EUR</p>
                      </div>
                    </button>
                    {expanded && (
                      <div className="bg-slate-50/50 px-4 py-3 border-t border-slate-200">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="text-slate-500 text-[10px] uppercase">
                              <th className="text-left py-1.5">Cliente</th>
                              <th className="text-left py-1.5">Tipo</th>
                              <th className="text-right py-1.5">Erogato</th>
                              <th className="text-right py-1.5">Provvigione</th>
                              <th className="text-left py-1.5">Beneficiario</th>
                              <th className="text-left py-1.5">Data</th>
                              <th className="text-left py-1.5">Stato</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-200">
                            {c.pratiche.map((p) => (
                              <tr key={p.id} className="hover:bg-white" data-testid={`report-comp-row-${p.id}`}>
                                <td className="py-1.5 font-medium text-slate-700">{p.cliente}</td>
                                <td className="py-1.5"><Badge variant="outline" className="text-[9px]">{p.tipo}</Badge></td>
                                <td className="py-1.5 text-right font-mono">{fmt(p.importo_erogato)} EUR</td>
                                <td className="py-1.5 text-right font-mono font-semibold text-green-700">{fmt(p.provvigione_importo)} EUR</td>
                                <td className="py-1.5">
                                  <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded ${p.beneficiario_nome === 'Agenzia' ? 'bg-amber-100 text-amber-700' : 'bg-sky-100 text-sky-700'}`}>
                                    {p.beneficiario_nome}
                                  </span>
                                </td>
                                <td className="py-1.5 text-slate-500">{p.data_erogazione || '-'}</td>
                                <td className="py-1.5 text-slate-500">{p.stato}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* DETTAGLIO PER BENEFICIARIO (drill-down) */}
      {detailed && (
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-2 mb-3">
            <Users size={16} className="text-sky-600" />
            <h3 className="text-sm font-semibold text-slate-800">Dettaglio Provvigioni: Agenzia + Collaboratori</h3>
            <span className="text-[11px] text-slate-400">(clicca per vedere le pratiche su cui hanno guadagnato)</span>
          </div>
          {detailed.beneficiari.length === 0 ? (
            <p className="text-center text-slate-400 text-xs py-6">Nessun dato per il periodo selezionato</p>
          ) : (
            <div className="space-y-2">
              {detailed.beneficiari.map((b) => {
                const expanded = expandedBen[b.nome];
                const isAgenzia = b.tipo === 'agenzia';
                return (
                  <div key={b.nome} className={`border rounded-lg overflow-hidden ${isAgenzia ? 'border-amber-200' : 'border-sky-200'}`} data-testid={`report-beneficiario-${b.nome}`}>
                    <button
                      onClick={() => setExpandedBen(s => ({ ...s, [b.nome]: !s[b.nome] }))}
                      className={`w-full flex items-center gap-3 px-4 py-3 hover:${isAgenzia ? 'bg-amber-50' : 'bg-sky-50'} transition-colors text-left`}
                    >
                      {expanded ? <ChevronDown size={14} className="text-slate-500" /> : <ChevronRight size={14} className="text-slate-500" />}
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${isAgenzia ? 'bg-amber-100 text-amber-700' : 'bg-sky-100 text-sky-700'}`}>
                        {isAgenzia ? <Building2 size={14} /> : <User size={14} />}
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-slate-800">{b.nome}</p>
                        <p className="text-[11px] text-slate-500">{b.count} pratiche · {isAgenzia ? 'Provvigione Agenzia' : 'Segnalatore / Collaboratore'}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-slate-400">Guadagno</p>
                        <p className={`text-base font-bold ${isAgenzia ? 'text-amber-700' : 'text-sky-700'}`}>{fmt(b.totale_provvigione)} EUR</p>
                      </div>
                    </button>
                    {expanded && (
                      <div className={`px-4 py-3 border-t ${isAgenzia ? 'bg-amber-50/40 border-amber-200' : 'bg-sky-50/40 border-sky-200'}`}>
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="text-slate-500 text-[10px] uppercase">
                              <th className="text-left py-1.5">Cliente</th>
                              <th className="text-left py-1.5">Compagnia</th>
                              <th className="text-left py-1.5">Tipo</th>
                              <th className="text-right py-1.5">Erogato</th>
                              <th className="text-right py-1.5">Provvigione</th>
                              <th className="text-left py-1.5">Data</th>
                              <th className="text-left py-1.5">Stato</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-200">
                            {b.pratiche.map((p) => (
                              <tr key={p.id} className="hover:bg-white/60" data-testid={`report-ben-row-${p.id}`}>
                                <td className="py-1.5 font-medium text-slate-700">{p.cliente}</td>
                                <td className="py-1.5 text-slate-600">{p.compagnia}</td>
                                <td className="py-1.5"><Badge variant="outline" className="text-[9px]">{p.tipo}</Badge></td>
                                <td className="py-1.5 text-right font-mono">{fmt(p.importo_erogato)} EUR</td>
                                <td className="py-1.5 text-right font-mono font-semibold text-green-700">{fmt(p.provvigione_importo)} EUR</td>
                                <td className="py-1.5 text-slate-500">{p.data_erogazione || '-'}</td>
                                <td className="py-1.5 text-slate-500">{p.stato}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
